import { useState, useEffect, useCallback } from "react";
import { ethers } from "ethers";
import { toast } from "react-toastify";
import { Info } from "lucide-react";
import { Tooltip } from "react-tooltip";
import { useNativePrices } from "./nativePriceCache";
import ChainSelector from "./ChainSelector";
import { useWithdrawQuote } from "./useBridgeQuote";
import { trackBridgeStatus } from "./bridgeStatusTracker";
import {
  BRIDGE_CHAINS,
  NIBIRU_EID,
  NIBIRU_EVM_CHAIN_ID,
  NIBIRU_EVM_RPC,
  NIBIRU_USDC_ADDRESS,
  NIBIRU_USDC_ADAPTER,
  ERC20_ABI,
  OFT_ABI,
  USDC_DECIMALS,
  buildSendParam,
  execFee,
} from "./bridgeConstants";
import {
  buildWithdrawOptions,
  quoteMinAmountLD,
  ensureChain,
  waitForChain,
  isNetworkError,
} from "./bridgeHelpers";
import { truncateWalletAddress } from "../functions/Functions";
import { PurpleButton } from "../Buttons/PurpleButton";
import { fetchBankBalance } from "../../evm/services/tradingEvmService";

function smartFormat(val) {
  if (!val || val === 0) return "0.00";
  if (val >= 1) return val.toFixed(2);
  let d = 2;
  while (d < 10 && Number(val.toFixed(d)) === 0) d++;
  return val.toFixed(Math.min(d + 1, 10));
}

const MAX_RETRIES = 2;
const RETRY_DELAY = 3000;

export default function WithdrawFlow({ walletAddress }) {
  const [selectedChain, setSelectedChain] = useState(BRIDGE_CHAINS[0]);
  const [amount, setAmount] = useState("");
  const [nibiruUsdcBalance, setNibiruUsdcBalance] = useState("0");
  const [nibiBalance, setNibiBalance] = useState("0");
  const [destBalances, setDestBalances] = useState({});
  const [loadingBalance, setLoadingBalance] = useState(false);
  const [loadingDestBalances, setLoadingDestBalances] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [executionStep, setExecutionStep] = useState("");

  // On-chain quote
  const { quote, loading: quoteLoading, error: quoteError } = useWithdrawQuote({
    destChain: selectedChain,
    amount,
    walletAddress,
  });

  // Fetch USDC.e (ERC20 + bank) and NIBI balance on Nibiru
  const fetchBalances = useCallback(async () => {
    if (!walletAddress) return;
    setLoadingBalance(true);
    try {
      const provider = new ethers.JsonRpcProvider(NIBIRU_EVM_RPC, { chainId: 6900, name: "nibiru", ensAddress: null });
      const bech32Addr = window.__nibiru_bech32 || null;
      const [erc20Bal, nibiBal, bankBal] = await Promise.all([
        new ethers.Contract(NIBIRU_USDC_ADDRESS, ERC20_ABI, provider).balanceOf(walletAddress),
        provider.getBalance(walletAddress),
        fetchBankBalance(bech32Addr),
      ]);
      const erc20 = parseFloat(ethers.formatUnits(erc20Bal, USDC_DECIMALS));
      setNibiruUsdcBalance((erc20 + bankBal).toString());
      setNibiBalance(ethers.formatEther(nibiBal));
    } catch {
      setNibiruUsdcBalance("0");
      setNibiBalance("0");
    } finally {
      setLoadingBalance(false);
    }
  }, [walletAddress]);

  // Fetch USDC balances on destination chains
  const fetchDestBalances = useCallback(async () => {
    if (!walletAddress) return;
    setLoadingDestBalances(true);
    const bals = {};
    const promises = BRIDGE_CHAINS.map(async (chain) => {
      try {
        const provider = new ethers.JsonRpcProvider(chain.rpcUrl, {
          chainId: chain.chainId,
          name: chain.id,
          ensAddress: null,
        });
        const contract = new ethers.Contract(chain.usdcAddress, ERC20_ABI, provider);
        const bal = await contract.balanceOf(walletAddress);
        bals[chain.id] = ethers.formatUnits(bal, chain.usdcDecimals || USDC_DECIMALS);
      } catch {
        bals[chain.id] = "0";
      }
    });
    await Promise.all(promises);
    setDestBalances(bals);
    setLoadingDestBalances(false);
  }, [walletAddress]);

  useEffect(() => {
    fetchBalances();
    fetchDestBalances();
  }, [fetchBalances]);

  const currentBalance = parseFloat(nibiruUsdcBalance);
  const currentNibi = parseFloat(nibiBalance);
  const inputAmount = parseFloat(amount) || 0;

  // Check if user has enough NIBI for the LZ fee
  const insufficientNibi =
    quote && currentNibi < quote.feeDisplay;

  // Button state
  const getButtonState = () => {
    if (!amount || inputAmount <= 0)
      return { label: "Enter amount", disabled: true };
    if (inputAmount > currentBalance)
      return { label: "Insufficient balance", disabled: true };
    if (quoteLoading)
      return { label: "Estimating...", disabled: true };
    if (quoteError)
      return { label: "Quote unavailable", disabled: true };
    if (insufficientNibi)
      return { label: "Insufficient NIBI for fee", disabled: true };
    if (executing)
      return { label: executionStep, disabled: true };
    return { label: "Withdraw", disabled: false };
  };

  const { label: btnLabel, disabled: btnDisabled } = getButtonState();

  // Native token USD prices (cached 6h, fetched via backend CoinGecko proxy)
  const nativePrices = useNativePrices();

  const feeUsd = quote && nativePrices.NIBI
    ? (quote.feeDisplay * nativePrices.NIBI).toFixed(2)
    : null;

  // Execute withdraw with retry
  const executeWithdraw = async () => {
    const provider = new ethers.BrowserProvider(window.ethereum);

    setExecutionStep("Switching to Nibiru...");
    await ensureChain(provider, NIBIRU_EVM_CHAIN_ID);
    await waitForChain(provider, NIBIRU_EVM_CHAIN_ID);

    const signer = await provider.getSigner();
    const tokensToSend = ethers.parseUnits(amount, USDC_DECIMALS);
    const recipientBytes32 = ethers.zeroPadValue(walletAddress.toLowerCase(), 32);
    const options = buildWithdrawOptions();
    const readProvider = new ethers.JsonRpcProvider(NIBIRU_EVM_RPC, { chainId: 6900, name: "nibiru", ensAddress: null });
    const oftRead = new ethers.Contract(NIBIRU_USDC_ADAPTER, OFT_ABI, readProvider);

    // Fresh quote right before sending
    setExecutionStep("Bridging...");
    const { minAmountLD } = await quoteMinAmountLD(
      oftRead,
      selectedChain.eid,
      recipientBytes32,
      tokensToSend,
      options
    );
    const sendParam = buildSendParam(
      selectedChain.eid,
      recipientBytes32,
      tokensToSend,
      minAmountLD,
      options
    );
    const quoteSendResult = await oftRead.quoteSend(sendParam, false);
    const nativeFeeCeil = execFee(ethers.toBigInt(quoteSendResult.nativeFee));

    // Execute OFT send (no approval needed for withdraw)
    const oftSigner = new ethers.Contract(NIBIRU_USDC_ADAPTER, OFT_ABI, signer);
    const tx = await oftSigner.send(
      sendParam,
      [nativeFeeCeil, ethers.toBigInt(0)],
      await signer.getAddress(),
      {
        value: nativeFeeCeil,
        gasLimit: ethers.toBigInt(500000),
      }
    );
    return tx.hash;
  };

  const handleWithdraw = async () => {
    if (!quote) return;
    setExecuting(true);

    try {
      for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
        try {
          const txHash = await executeWithdraw();
          setAmount("");
          fetchBalances();

          // Track via LayerZero Scan — shows persistent toast until delivered
          trackBridgeStatus(NIBIRU_EID, txHash, "withdraw", () => {
            fetchBalances();
            window.dispatchEvent(new CustomEvent("bridge-balance-update"));
          });
          break;
        } catch (err) {
          if (isNetworkError(err) && attempt < MAX_RETRIES) {
            await new Promise((r) => setTimeout(r, RETRY_DELAY));
            continue;
          }
          throw err;
        }
      }
    } catch (err) {
      console.error("Withdraw error:", err);
      if (err.code === 4001 || err.code === "ACTION_REJECTED") {
        toast.warn("Transaction cancelled.");
      } else {
        toast.error(err.shortMessage || err.message || "Withdrawal failed.");
      }
    } finally {
      setExecuting(false);
      setExecutionStep("");
    }
  };

  const handleMax = () => {
    if (currentBalance > 0) {
      setAmount(
        currentBalance
          .toFixed(USDC_DECIMALS)
          .replace(/\.?0+$/, "")
      );
    }
  };

  return (
    <div className="flex flex-col gap-[16px]">
      {/* Inner card */}
      <div className="bg-[#f8f8f8] rounded-[12px] border border-[rgba(0,0,0,0.06)] p-4">
        <div className="flex items-center gap-1.5 mb-3 text-[12px]">
          <span className="text-[#aaa] uppercase tracking-wide">To</span>
          <span className="text-[#888] font-mono">{truncateWalletAddress(walletAddress)}</span>
        </div>

        <ChainSelector
          chains={BRIDGE_CHAINS}
          selected={selectedChain}
          onSelect={setSelectedChain}
          balances={destBalances}
          loadingBalances={loadingDestBalances}
        />

        {/* Amount input */}
        <div className="mt-4 flex items-end justify-between">
          <div>
            <input
              type="text"
              inputMode="decimal"
              placeholder="0.00"
              value={amount}
              onChange={(e) => {
                const val = e.target.value;
                if (/^\d*\.?\d*$/.test(val)) setAmount(val);
              }}
              className="text-[28px] font-semibold text-[#111] bg-transparent outline-none w-[160px] placeholder-[#ccc]"
            />
            <div className="text-[12px] text-[#888] mt-0.5">
              {inputAmount > 0 ? `$${inputAmount.toFixed(2)}` : "$0.00"}
            </div>
          </div>
          <div className="text-right">
            <button
              onClick={handleMax}
              className="text-[12px] font-semibold text-[#5b1dee] border border-[#5b1dee] rounded-[8px] px-3 py-1.5 hover:bg-[#f8f4fc] transition-colors cursor-pointer"
            >
              Max
            </button>
            <div className="text-[11px] text-[#888] mt-1.5">
              Balance:{" "}
              <span className="font-semibold text-[#111]">
                {loadingBalance
                  ? "..."
                  : `${smartFormat(parseFloat(nibiruUsdcBalance))} USDC`}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Receiving address */}
      <div className="bg-[#faf8fc] rounded-[12px] border border-[rgba(91,29,238,0.06)] px-4 py-3">
        <div className="flex items-center justify-between text-[13px]">
          <span className="text-[#666]">Receiving address</span>
          <span className="text-[#111] font-medium font-mono text-[12px]">
            {truncateWalletAddress(walletAddress)}
          </span>
        </div>
      </div>

      {/* Info section */}
      <div className="bg-[#faf8fc] rounded-[12px] border border-[rgba(91,29,238,0.06)] px-4 py-3 flex flex-col gap-2">
        <div className="flex items-center justify-between text-[13px]">
          <span className="text-[#666]">Estimated Time</span>
          <span className="text-[#111] font-medium">~2 min</span>
        </div>
        <div className="flex items-center justify-between text-[13px]">
          <span className="text-[#666] flex items-center gap-1">
            Bridge Fee{" "}
            <Info
              size={12}
              className="text-[#aaa] cursor-help"
              data-tooltip-id="withdraw-fee-tooltip"
            />
            <Tooltip
              id="withdraw-fee-tooltip"
              place="top"
              style={{ maxWidth: 240, fontSize: 12, zIndex: 999999, backgroundColor: "rgba(255, 255, 255, 0.92)", color: "#333", borderRadius: 10, padding: "8px 12px", backdropFilter: "blur(8px)", boxShadow: "0 2px 12px rgba(0,0,0,0.1)" }}
              content="A small fee paid in NIBI to LayerZero executors for delivering your tokens to the destination chain."
            />
          </span>
          <span className="text-[#111] font-medium">
            {quoteLoading ? (
              <span className="text-[#bbb]">Estimating...</span>
            ) : quote ? (
              <>
                {quote.feeDisplay.toFixed(8)} NIBI
                {feeUsd && (
                  <span className="text-[#888] font-normal"> (~${feeUsd})</span>
                )}
              </>
            ) : (
              "—"
            )}
          </span>
        </div>
        <div className="flex items-center justify-between text-[13px]">
          <span className="text-[#666] flex items-center gap-1">
            Min. Received{" "}
            <Info
              size={12}
              className="text-[#aaa] cursor-help"
              data-tooltip-id="min-received-tooltip"
            />
            <Tooltip
              id="min-received-tooltip"
              place="top"
              style={{ maxWidth: 260, fontSize: 12, zIndex: 999999, backgroundColor: "rgba(255, 255, 255, 0.92)", color: "#333", borderRadius: 10, padding: "8px 12px", backdropFilter: "blur(8px)", boxShadow: "0 2px 12px rgba(0,0,0,0.1)" }}
              content="The minimum you will receive after a Stargate Protocol Fee and a 1.00% slippage buffer. The transaction reverts on-chain if this threshold is not met."
            />
          </span>
          <span className="text-[#111] font-medium">
            {quoteLoading ? (
              <span className="text-[#bbb]">Estimating...</span>
            ) : quote ? (
              `${smartFormat(quote.minReceivedDisplay)} USDC`
            ) : (
              "—"
            )}
          </span>
        </div>
      </div>

      {/* Error */}
      {quoteError && inputAmount > 0 && inputAmount <= currentBalance && (
        <div className="text-[12px] text-red-500 text-center px-2">
          {quoteError}
        </div>
      )}

      {/* CTA */}
      <PurpleButton
        text={
          <span className="flex w-full items-center justify-center gap-2 text-[14px]">
            {(quoteLoading || executing) && (
              <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            )}
            {btnLabel}
          </span>
        }
        disabled={btnDisabled}
        onClick={handleWithdraw}
        className="w-full h-[46px] !rounded-[12px]"
      />
    </div>
  );
}
